import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";
import useTheme from "@/utils/useTheme";
import Footer from "@/components/sections/Footer";
import { Toaster } from "@/components/ui/sonner";

/**
 * Shared layout for legal / policy pages (Terms, Refund, Privacy, Shipping).
 * Uses the site's liquid-glass dark theme for brand consistency.
 */
const LegalPageLayout = ({ title, lastUpdated, children, testId }) => {
  useTheme();
  return (
    <div className="min-h-screen bg-[#0A1029] text-white">
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 z-20 backdrop-blur-md bg-[#0A1029]/80">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-white/60 hover:text-white flex items-center gap-2 text-sm"
            data-testid="legal-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <span className="text-white/20">|</span>
          <h1 className="text-sm font-display text-white">{title}</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16" data-testid={testId}>
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-display tracking-tight mb-3">{title}</h1>
          {lastUpdated && (
            <p className="text-white/40 text-sm">Last updated: {lastUpdated}</p>
          )}
        </div>

        <article className="prose prose-invert max-w-none prose-headings:font-display prose-headings:tracking-tight
          prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-[#4ECDC4]
          prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-white
          prose-p:text-white/70 prose-p:leading-relaxed
          prose-li:text-white/70 prose-li:my-1
          prose-ul:my-4 prose-strong:text-white
          prose-a:text-[#4ECDC4] prose-a:no-underline hover:prose-a:underline">
          {children}
        </article>

        {/* Quick contact block — Razorpay wants contact info clearly visible on policy pages */}
        <div className="mt-14 p-6 rounded-2xl bg-white/[0.03] border border-white/10">
          <h3 className="text-lg font-display mb-4 text-white">Contact us</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <a href="mailto:info@tots360.in" className="flex items-start gap-3 text-white/70 hover:text-white">
              <Mail className="w-4 h-4 text-[#4ECDC4] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs">Email</p>
                <p>info@tots360.in</p>
              </div>
            </a>
            <a href="tel:+918985709090" className="flex items-start gap-3 text-white/70 hover:text-white">
              <Phone className="w-4 h-4 text-[#4ECDC4] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs">Phone (India)</p>
                <p>+91 89857 09090 / +91 89857 09595</p>
              </div>
            </a>
            <div className="flex items-start gap-3 text-white/70">
              <MapPin className="w-4 h-4 text-[#4ECDC4] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs">Address</p>
                <p>Rev &amp; Dev Children Clinic, 2nd Floor, Fairmount Fortune One, Czech Colony, Sanath Nagar, Hyderabad — 500018, Telangana, India</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

export default LegalPageLayout;
